// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { cloudRevision } from '../../api/events';
import type { CloudInfo, FrameVisibilityList, GsplatInfo, Job, MeshInfo, PanoramaInfo, PoseGraph, SolidOpening, SolidSpace, SolidWall, VisibleFrame } from '../../api/types';
import type { CaseData } from '../../app/bygning/useCaseData';
import { isOwnImport } from '../../app/bygning/ownImports';
import { useImportFinished } from '../../app/bygning/useImportFinished';
import { useCanEdit } from '../../app/CaseRoleContext';
import { useJobs } from '../../app/JobsContext';
import { segmentHref } from '../../app/links';
import { useAsync } from '../../app/useAsync';
import { LEGEND_TITLE, labelLegendItems, legendItems, legendKind, modeToken } from '../../bygning/lens';
import type { ModeId } from '../../bygning/modes';
import { roomName, roomsOf } from '../../bygning/rooms';
import { togglePart, type Selection } from '../../bygning/selection';
import {
  centroidsOf,
  chooseStoreys,
  instanceExtents,
  mergeInstanceExtents,
  mergeRoomExtents,
  partRooms,
  roomExtents,
} from '../../bygning/spatial';
import { importRefresh } from '../../bygning/bimImport';
import { kindColorToken, modelLayerOn, modelLayers, modelStatus, splitMeshes, type LayerLoad } from '../../bygning/modelLayers';
import {
  EMPTY_TRACKER,
  beginSave,
  defaultCameraZ,
  escapeAction,
  isSaving,
  mergePending,
  settleSave,
  equirectProblem,
  fileProblem,
  formatMb,
  jpegSize,
  moveNudge,
  optimisticPlacement,
  panoramaYaw,
  placementErrorText,
  snapYaw,
  uploadQuery,
  upsertPanorama,
  yawFromDrag,
} from '../../bygning/panoramaPlacement';
import { planWallSet } from '../../bygning/planWalls';
import { pickPlanElement } from '../../bygning/geometri';
import { bimFeatures } from '../../bygning/snapping';
import { planSlabs } from '../../bygning/planSlabs';
import { elementDetails } from '../../bygning/elementDetails';
import { elementsDrawn, elementsSummary } from '../../bygning/elementGeometry';
import { clipForZRange, planScope, storeyLevels, storeyZRange } from '../../bygning/storeyClip';
import { planSpaces, spaceAt } from '../../bygning/spaces';
import { deriveStoreys, markersOnStorey, onStorey, storeyById, storeyOfRoom, type Storey } from '../../bygning/storeys';
import { Button } from '../../components/Button';
import { PlacementPanel, type PlacementField, type PlacementView } from '../../components/bygning/PlacementPanel';
import { CanvasLegend } from '../../components/bygning/CanvasLegend';
import { StoreyPicker } from '../../components/bygning/StoreyPicker';
import { MeasurePanel } from '../../components/bygning/MeasurePanel';
import { PanoramaOverlay } from '../../components/bygning/PanoramaOverlay';
import { SourceImages } from '../../components/bygning/SourceImages';
import { useGroundSettings } from '../../components/bygning/settings/SettingsContext';
import {
  DEFAULT_SETTINGS,
  GHOST_STRENGTH,
  ghostOpacityOf,
  RENDERABLE_TYPES,
  activeLabelSource,
  cloudOn,
  densityBudget,
  labelSourcesFor,
  pickPrimary,
  pointSizeOf,
  resolveColouring,
  sameBox,
  settingsReducer,
  type ClipBox,
  type GroundFacts,
} from '../../components/bygning/settings/settingsState';
import {
  buildColorLut,
  dominantPartCloud,
  instanceOwners,
  parseCssColor,
  partInstances,
  type ColorLut,
  type Rgb,
} from '../../viewport/colorLut';
import {
  buildingElementTallies,
  elementCategoryForImportedKind,
  visibleOpenings,
  visibleSlabs,
  visibleSpaces,
  visibleWalls,
  wallStoreysByGuid,
} from '../../viewport/buildingElements';
import { INITIAL_STOREY_FILTER, isElementVisible, toElementFilterState, type ElementFilterState } from '../../viewport/elementFilter';
import { InstanceIndex } from '../../viewport/instanceIndex';
import { readLabelPalette } from '../../viewport/labelColors';
import { describePlacementDa, resolvePlacement, stepPanorama } from '../../viewport/panorama';
import type { PanoramaMarker } from '../../viewport/PanoramaScene';
import type { PageBuffers } from '../../viewport/PointCloudScene';
import type { CloudStreamState } from '../../viewport/useCloudStream';
import { cloudIdentity, meshIdentity } from '../../viewport/viewerCache';
import {
  Viewport,
  type MeshLayerState,
  type PanoramaLayerState,
  type PointerHits,
  type RoomLabel,
  type SplatLayerState,
  type ViewportElements,
  type ViewportHandle,
  type ViewportLayer,
  type ViewportMesh,
  type ViewportSplat,
  type WorldPoint,
} from '../../viewport/Viewport';
import type { GroundProps } from './modeTypes';
import styles from './Ground.module.css';
import { ElementChip } from '../../components/bygning/ElementChip';
import { useBuildingElements } from './useBuildingElements';
import { MeasureButtons } from './MeasureButtons';
import { MeasureLayer } from './MeasureLayer';
import { PlanLayer, type PlanDragKind, type PlanMarker } from './PlanLayer';
import { PlanWalls } from './PlanWalls';
import { CadToolLayer } from './CadToolLayer';
import { GeometriLayer } from './GeometriLayer';
import { useGeometriEditor } from '../../app/bygning/GeometriEditorContext';
import { useBuildingModel } from './useBuildingModel';
import { useMeasureTool } from './useMeasureTool';
import { useServerSpatial } from './useServerSpatial';

/** Colours to fall back on when a token cannot be read (linear rgb; never reached in the browser). */
const FALLBACK = {
  ghost: [0.27, 0.28, 0.3] as Rgb,
  accent: [0.03, 0.13, 0.67] as Rgb,
  neutral: [0.13, 0.15, 0.17] as Rgb,
};

/**
 * Placér 360° (frontend-ayw.3): picking a marker (and Flyt-ing it), or a
 * new JPEG on its way in — where it goes (`at`, world x/y), its height and
 * heading, and the upload's progress.
 */
type Placing =
  | { kind: 'select'; id: number | null; moving: boolean }
  | {
      kind: 'add';
      file: File;
      detail: string;
      at: [number, number] | null;
      z: number;
      yaw: number;
      progress: number | null;
    };

/** A marker being dragged or turned: its live position and heading. */
interface DragPreview {
  id: number;
  x: number;
  y: number;
  yaw: number | null;
}

/** How close (CSS px) a click must land to a marker to be the marker's. */
const MARKER_CLICK_PX = 14;

/** What a click target is: a part (Ressourcer, Gennemgang) or a room (Overblik). */
type HitTarget = { kind: 'part'; code: string; typeId: number } | { kind: 'room'; id: number };

const NOOP = () => {};
/** No panorama markers: the CAD plan (Rediger › Geometri) keeps its canvas to the model. */
const NO_MARKERS: PlanMarker[] = [];


/**
 * The instances and rooms `Label` clouds to zip onto `primary`, each only
 * when its point count matches — the same exclusion rule the old viewport
 * page applied. Equal length is necessary, not sufficient, for index
 * alignment; colouring by a misaligned cloud would draw a plausible and
 * entirely fictional segmentation, so anything that fails it is left out.
 * The instances cloud is the one the survey's parts point at, else
 * `instances`.
 */
function pickLabelClouds(
  clouds: readonly CloudInfo[] | null,
  primary: CloudInfo | null,
  partCloud: string | null,
): { inst: CloudInfo | null; rooms: CloudInfo | null } {
  if (!primary) return { inst: null, rooms: null };
  const aligned = (clouds ?? []).filter((c) => c.type === 'Label' && c.point_count === primary.point_count);
  const inst = (partCloud ? aligned.find((c) => c.name === partCloud) : undefined) ?? aligned.find((c) => c.name === 'instances') ?? null;
  const rooms = aligned.find((c) => c.name === 'rooms') ?? null;
  return { inst, rooms: rooms && rooms !== inst ? rooms : null };
}

/** The types a mode emphasises: null = no focus (all in colour). */
function focusTypes(mode: ModeId, sel: Selection, data: CaseData): number[] | null {
  if (mode === 'ressourcer' || mode === 'gennemgang') return sel.typeId === null ? null : [sel.typeId];
  if (mode === 'miljoe') {
    const sample = data.samples.find((s) => s.id === sel.sampleId);
    if (!sample) return null;
    return data.types.filter((t) => sample.type_ids.includes(t.id) || t.sample_ids.includes(sample.id)).map((t) => t.id);
  }
  return null;
}

/** Bumps on every `<html>` theme change, after the attribute is set (the parent's effect runs after ours). */
function useThemeTick(): number {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const observer = new MutationObserver(() => setTick((t) => t + 1));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return tick;
}

/** A token name → linear rgb, read off `<html>` once per call site (cached by name). */
function tokenReader(): (name: string | null) => Rgb | null {
  const style = typeof getComputedStyle === 'function' ? getComputedStyle(document.documentElement) : null;
  const cache = new Map<string, Rgb | null>();
  return (name) => {
    if (!name || !style) return null;
    if (!cache.has(name)) cache.set(name, parseCssColor(style.getPropertyValue(name)));
    return cache.get(name) ?? null;
  };
}

/**
 * The ground layer of the Bygning shell: the scanned building, full-bleed
 * under the tiles, cleared to the page surface (PLAN.md decision 5) and
 * coloured per survey type by the mode — Overblik by affaldshierarki,
 * Ressourcer by the lens, Gennemgang by review status, Miljø by the linked
 * samples' stage, Rapport as a quiet ghost. The type in focus
 * stays bright, the rest lerps toward `--color-ghost`, picked parts take the
 * accent, and a room focus fades the other rooms and frames the room — in
 * Ressourcer it hides them instead, unless "Vis omgivelser" (`showOutside`)
 * is on; its other filters (`ghostCodes`: the live parts outside the set)
 * ghost what they leave out. A storey focus (`?storey=`, no room) fades all but the storey's
 * rooms and frames them;
 * the storeys come from `GET /storeys` (or, with none stored, are derived
 * from the room centroids, `deriveStoreys`) and are published to the
 * settings context. Room and instance centroids/boxes come from `GET /rooms`
 * and `InstanceInfo` before the stream lands, the streamed pages filling in
 * what the server does not answer (`bygning/spatial.ts`).
 *
 * Over the model, top left: the mode's colour legend (`legendKind`:
 * Ressourcer's lens, Gennemgang's status, Miljø's sample stages) above the
 * tools: Vælg punkt, Mål afstand / Mål areal (`useMeasureTool`, snapping to
 * the scan and the building model's walls and openings), and Klippeboks /
 * Klip (the clipping box's handles in the model, and the cut). On the
 * locked plan the building model's walls and openings are drawn
 * (`PlanWalls`) and Placér 360° snaps to them.
 *
 * Absorbs the retired viewport page (decision 8): the cloud and label-cloud
 * choice, mesh / panorama markers from Modelindstillinger (splat and pose
 * graph are still drawn from the settings, which no longer offer them),
 * the `?pano=` immersive mode and the `?captureFor=` thumbnail capture.
 *
 * Clicks: in Ressourcer and Gennemgang a click on a part of the selected type
 * toggles it in the picks, a click on another type selects that type, and a
 * shift-click selects it and toggles the part (picking across types). In
 * Overblik a click on a room focuses it.
 */
export function Ground({ data, sel, setSel, mode, leftInset, leftPhoneBar, ghostCodes, legendTypeIds }: GroundProps) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const canEdit = useCanEdit();
  const themeTick = useThemeTick();
  const viewportRef = useRef<ViewportHandle>(null);
  const groundRef = useRef<HTMLDivElement>(null);

  // Settings live in the shell's SettingsProvider when it has one (the gear
  // panel edits them there); without one the Ground keeps its own copy.
  const bridge = useGroundSettings();
  const [localState, localDispatch] = useReducer(settingsReducer, DEFAULT_SETTINGS);
  const settings = bridge?.state ?? localState;
  const dispatch = bridge?.dispatch ?? localDispatch;
  const publish: (patch: Partial<GroundFacts>) => void = bridge?.publish ?? NOOP;

  const quiet = mode === 'rapport';

  // ------------------------------------------------------------- clouds --

  const cloudsRead = useAsync<CloudInfo[]>((signal) => api.clouds(signal), []);
  const { cloudRevisions, panoramasChange } = useJobs();
  const seenRevisions = useRef(cloudRevisions);
  const reloadClouds = cloudsRead.reload;
  useEffect(() => {
    // `clouds.changed`: re-read the list (new label clouds); the layer's
    // revision below restarts its stream.
    if (seenRevisions.current === cloudRevisions) return;
    seenRevisions.current = cloudRevisions;
    reloadClouds();
  }, [cloudRevisions, reloadClouds]);

  const clouds = cloudsRead.data ?? null;
  const primary = useMemo(() => pickPrimary(clouds), [clouds]);
  const partCloud = useMemo(() => dominantPartCloud(data.types), [data.types]);
  const { inst: instInfo, rooms: roomsInfo } = useMemo(
    () => pickLabelClouds(clouds, primary, partCloud),
    [clouds, primary, partCloud],
  );
  const instCloud = instInfo?.name ?? null;
  const roomCloud = roomsInfo?.name ?? null;

  // ------------------------------------------------------- other layers --

  const meshesRead = useAsync<MeshInfo[]>((signal) => api.meshes(signal), []);
  // A BIM import that finishes while the case is open (Importér BIM-model,
  // or another client's): re-read the mesh list, then download again just
  // the meshes the job wrote (`result.outputs`; same names, new geometry) —
  // a category the re-import dropped leaves the list and is never fetched.
  // An import with `meshes: false` touches no mesh. Storeys and spaces
  // re-read on every finished job (useServerSpatial).
  const [meshRevs, setMeshRevs] = useState<Readonly<Record<string, number>>>({});
  // Waiting for the re-read list: the names to bump, and whether this page started the import.
  const pendingImport = useRef<{ names: Set<string>; own: boolean } | null>(null);
  // An own import's categories still downloading; the camera frames the
  // model once the last lands (cleared by any camera input of the user's).
  const frameAfter = useRef<Set<string> | null>(null);
  const reloadMeshes = meshesRead.reload;
  useImportFinished(
    useCallback(
      (finished: Job[]) => {
        const names = new Set<string>();
        let own = false;
        for (const job of finished) {
          const written = importRefresh(job.result).meshes;
          for (const n of written) names.add(n);
          if (written.length > 0 && isOwnImport(job.id)) own = true;
        }
        if (names.size === 0) return;
        const prev = pendingImport.current;
        pendingImport.current = { names: new Set([...(prev?.names ?? []), ...names]), own: own || (prev?.own ?? false) };
        reloadMeshes();
      },
      [reloadMeshes],
    ),
  );
  const gsplatsRead = useAsync<GsplatInfo[]>((signal) => api.gsplats(signal), []);
  const panoramasRead = useAsync<PanoramaInfo[]>((signal) => api.panoramas(signal), []);
  // The list as shown: the server's, with placements made here applied ahead
  // of the answer (optimistic, rolled back on an error) — re-read on
  // `panoramas.changed` from any client.
  const [panoList, setPanoList] = useState<PanoramaInfo[] | null>(null);
  // Placements sent and not yet answered (`bygning/panoramaPlacement.ts`):
  // a re-read keeps showing them until their latest save settles.
  const saveTracker = useRef(EMPTY_TRACKER);
  const [saveTick, setSaveTick] = useState(0);
  useEffect(() => {
    if (panoramasRead.data) setPanoList(mergePending(panoramasRead.data, saveTracker.current.pending));
  }, [panoramasRead.data]);
  const reloadPanoramas = panoramasRead.reload;
  const seenPanoramasChange = useRef(panoramasChange);
  useEffect(() => {
    if (seenPanoramasChange.current === panoramasChange) return;
    seenPanoramasChange.current = panoramasChange;
    reloadPanoramas();
  }, [panoramasChange, reloadPanoramas]);
  const panoById = useMemo(() => new Map((panoList ?? []).map((p) => [p.id, p])), [panoList]);
  // The pose graph is fetched the first time it is switched on, not before.
  const [poseWanted, setPoseWanted] = useState(false);
  useEffect(() => {
    if (settings.posegraph) setPoseWanted(true);
  }, [settings.posegraph]);
  const poseRead = useAsync<PoseGraph | null>(
    (signal) => (poseWanted ? api.posegraph(signal) : Promise.resolve(null)),
    [poseWanted],
  );

  // The scan's own mesh (`Mesh` in Lag) and the imported model's category
  // meshes (`imported/<source>/<slug>`, Lag › Model; frontend-ayw.7).
  const meshSplit = useMemo(() => splitMeshes(meshesRead.data), [meshesRead.data]);
  const mesh = meshSplit.scan;
  const model = useMemo(() => modelLayers(meshSplit), [meshSplit]);
  const splat = gsplatsRead.data?.[0] ?? null;
  const planLock = settings.planLock;

  // Bygningsdele (frontend-t02): the persisted per-category/storey filter,
  // governing the detected model (below) and, through
  // `elementCategoryForImportedKind`, an imported Revit/IFC model's matching
  // categories (acceptance criterion 3) — a wall/slab/roof/space/door/window
  // layer the user hid stays hidden, on top of its own Model switch.
  const elementFilter = useMemo(() => toElementFilterState(settings.buildingElements), [settings.buildingElements]);
  const modelLayerVisible = useCallback(
    (l: Parameters<typeof modelLayerOn>[1]) => {
      const category = elementCategoryForImportedKind(l.kind);
      return modelLayerOn(settings.modelLayers, l, planLock) && (category === null || isElementVisible(elementFilter, category, null));
    },
    [settings.modelLayers, planLock, elementFilter],
  );

  // Any mesh on screen counts as content (the tools need the model's bounds).
  const [meshContent, setMeshContent] = useState(false);
  // The building model's elements are in the scene (frontend-r22.27): content too.
  const [elementsShown, setElementsShown] = useState(false);
  // Every model category's download, by mesh name, for the status line.
  const [modelLoads, setModelLoads] = useState<Readonly<Record<string, LayerLoad>>>({});
  // Sticky: once a download has started, hiding the layer keeps the blob.
  const [meshRequested, setMeshRequested] = useState(false);
  useEffect(() => {
    if (settings.mesh && mesh) setMeshRequested(true);
  }, [settings.mesh, mesh]);
  // Each model category downloads the first time it is on (lazily: the
  // plan's hidden fittings are never fetched unless asked for), then stays.
  const [modelRequested, setModelRequested] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    const want = model.filter((l) => modelLayerVisible(l)).map((l) => l.name);
    setModelRequested((prev) => (want.every((n) => prev.has(n)) ? prev : new Set([...prev, ...want])));
  }, [model, modelLayerVisible]);
  // The re-read list has landed: bump the written meshes it still lists.
  useEffect(() => {
    const pending = pendingImport.current;
    if (!pending || !meshesRead.data) return;
    pendingImport.current = null;
    const listed = new Set(meshesRead.data.map((m) => m.name));
    const bump = [...pending.names].filter((n) => listed.has(n));
    if (bump.length === 0) return;
    setMeshRevs((revs) => {
      const next = { ...revs };
      for (const n of bump) next[n] = (next[n] ?? 0) + 1;
      return next;
    });
    if (pending.own) {
      // Frame once the bumped categories that download (the ones on, or
      // already fetched) have landed; one on nobody asked for never loads.
      const want = model.filter((l) => modelLayerVisible(l)).map((l) => l.name);
      const loading = new Set(bump.filter((n) => want.includes(n) || modelRequested.has(n)));
      frameAfter.current = loading;
      if (loading.size === 0) {
        frameAfter.current = null;
        viewportRef.current?.frameAll();
      }
    }
    // Keyed on the list only: the layers are read as they are when it lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meshesRead.data]);
  // Any camera input of the user's cancels a pending frame.
  const framePending = frameAfter.current !== null;
  useEffect(() => {
    const handle = viewportRef.current;
    if (!framePending || !handle) return;
    return handle.onUserCamera(() => {
      frameAfter.current = null;
    });
  }, [framePending, meshRevs]);

  const splatWanted = settings.render === 'splat' && splat !== null;
  const [splatRequested, setSplatRequested] = useState(false);
  const [splatLoaded, setSplatLoaded] = useState(false);
  useEffect(() => {
    if (splatWanted) setSplatRequested(true);
  }, [splatWanted]);

  // ------------------------------------------------------ instance index --

  const indexRef = useRef<InstanceIndex | null>(null);
  if (indexRef.current === null) indexRef.current = new InstanceIndex();
  const index = indexRef.current;
  const [indexRev, setIndexRev] = useState(0);
  const bumpTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bumpIndex = useCallback(() => {
    // Pages land in bursts; re-derive centroids, labels and bounds at most
    // every 200 ms rather than per page.
    if (bumpTimer.current !== null) return;
    bumpTimer.current = setTimeout(() => {
      bumpTimer.current = null;
      setIndexRev((r) => r + 1);
    }, 200);
  }, []);
  useEffect(
    () => () => {
      if (bumpTimer.current !== null) clearTimeout(bumpTimer.current);
    },
    [],
  );

  const labelNamesRef = useRef({ inst: instCloud, rooms: roomCloud });
  labelNamesRef.current = { inst: instCloud, rooms: roomCloud };
  const onPageAdded = useCallback(
    (layerId: string, buffers: PageBuffers) => {
      const { inst, rooms } = labelNamesRef.current;
      index.addPage(
        layerId,
        buffers.positions,
        inst ? (buffers.labelsBy?.[inst] ?? null) : null,
        rooms ? (buffers.labelsBy?.[rooms] ?? null) : null,
      );
      bumpIndex();
    },
    [index, bumpIndex],
  );
  const onLayerRemoved = useCallback(
    (layerId: string) => {
      index.removeLayer(layerId);
      bumpIndex();
    },
    [index, bumpIndex],
  );

  // The server's figures (whole cloud, before the stream) win; the streamed ones fill in.
  const serverSpatial = useServerSpatial(instCloud, roomCloud);
  // The building model's walls and openings (ruxd#11): the plan, the snapping, the 3D elements.
  const serverBuilding = useBuildingModel();
  // Rediger › Geometri (frontend-u0o.9): the editor's model — one read for
  // the editor and the plan, with the edits on their way already on it.
  const editor = useGeometriEditor();
  const cad = editor?.active === true;
  const editorModel = cad ? editor.model : null;
  const building = useMemo(
    // Read-only arrays: nothing here mutates them.
    () =>
      editorModel
        ? { walls: (editorModel.walls as SolidWall[] | null) ?? serverBuilding.walls, openings: (editorModel.openings as SolidOpening[] | null) ?? serverBuilding.openings }
        : serverBuilding,
    [editorModel, serverBuilding],
  );
  const spatial = useMemo(
    () => (editorModel?.spaces ? { ...serverSpatial, spaces: editorModel.spaces as SolidSpace[] } : serverSpatial),
    [editorModel, serverSpatial],
  );
  // The case has a building model to draw (it is the 3D model, frontend-r22.27).
  const hasElementRows = (building.walls?.length ?? 0) + (spatial.spaces?.length ?? 0) + (spatial.slabs?.length ?? 0) > 0;
  const serverInst = useMemo(
    () => (spatial.instances && instCloud ? instanceExtents(spatial.instances, partRooms(data.types, instCloud)) : null),
    [spatial.instances, instCloud, data.types],
  );
  const serverRooms = useMemo(() => (spatial.rooms ? roomExtents(spatial.rooms) : null), [spatial.rooms]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const instStats = useMemo(() => mergeInstanceExtents(serverInst, index.instances()), [index, indexRev, serverInst]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const roomStats = useMemo(() => mergeRoomExtents(serverRooms, index.rooms()), [index, indexRev, serverRooms]);

  // --------------------------------------------------------------- rooms --

  const rooms = useMemo(() => roomsOf(data.types, data.summary), [data.types, data.summary]);
  const roomLabelName = useCallback(
    (id: number) => roomName(rooms, id) ?? roomsInfo?.labels?.[String(id)] ?? `Rum ${id}`,
    [rooms, roomsInfo],
  );

  // Storeys: the stored ones (`GET /storeys`); with none stored, the room
  // centroids clustered by height (gap 7's heuristic). Re-derived per index
  // revision (roomStats is memoised on it), so they settle as the stream
  // lands; empty without a rooms cloud or for a single storey.
  const storeys = useMemo<Storey[]>(
    () =>
      chooseStoreys(spatial.storeys, () => {
        if (!roomCloud || roomStats.size === 0) return [];
        return deriveStoreys(
          [...roomStats].map(([id, s]) => ({ id, name: roomLabelName(id), z: s.centroid.z, zMin: s.min.z, zMax: s.max.z })),
        );
      }),
    [spatial.storeys, roomCloud, roomStats, roomLabelName],
  );
  useEffect(() => {
    publish({ storeys, spaces: spatial.spaces });
  }, [publish, storeys, spatial.spaces]);

  /**
   * The rooms in focus: the focused room, else the focused storey's rooms;
   * null for hele bygningen (or a storey id the heuristic no longer has).
   */
  const focusStorey = sel.room === null ? storeyById(storeys, sel.storey) : null;
  const focusIds = sel.room !== null ? [sel.room] : (focusStorey?.roomIds ?? null);
  // Keyed by value: the storeys are re-derived per index revision while the
  // stream lands, and an unchanged focus must not rebuild the colour table.
  const focusKey = focusIds === null ? null : `${sel.room !== null ? 'r' : 's'}${focusIds.join(',')}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const focusRooms = useMemo(() => focusIds, [focusKey]);
  const focusSet = useMemo(() => (focusRooms ? new Set(focusRooms) : null), [focusRooms]);
  // In Ressourcer a room focus hides the rest of the building unless "Vis
  // omgivelser" is on; a storey focus, and every other mode, ghosts it. Only
  // the primary layer is room-labelled, so a mesh or splat still shows the
  // surroundings.
  const hideOutside = mode === 'ressourcer' && sel.room !== null && !settings.showOutside;

  // ---------------------------------------------------------- panoramas --

  const [placing, setPlacing] = useState<Placing | null>(null);
  const [placeError, setPlaceError] = useState<string | null>(null);
  // Bumped to make the plan layer let go of a drag (Esc).
  const [dragCancel, setDragCancel] = useState(0);
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null);
  const dragPreviewRef = useRef(dragPreview);
  dragPreviewRef.current = dragPreview;

  const allMarkers = useMemo<PanoramaMarker[]>(
    () =>
      (panoList ?? []).flatMap((pano) => {
        // A marker being dragged is drawn where the pointer has it.
        const live =
          dragPreview && dragPreview.id === pano.id
            ? (optimisticPlacement(pano, {
                x: dragPreview.x,
                y: dragPreview.y,
                ...(dragPreview.yaw !== null ? { yaw_deg: dragPreview.yaw } : {}),
              }) ?? pano)
            : pano;
        const placement = resolvePlacement(live);
        return placement ? [{ id: pano.id, placement }] : [];
      }),
    [panoList, dragPreview],
  );
  // Under a storey focus (or a room's, on its storey) only the panoramas
  // taken on that storey are marked (frontend-ayw.2).
  const markerStorey = storeyById(storeys, sel.storey) ?? (sel.room !== null ? storeyOfRoom(storeys, sel.room) : null);
  const markerStoreyKey = markerStorey ? `${markerStorey.zMin}:${markerStorey.zMax}` : null;
  const markers = useMemo(
    () => markersOnStorey(allMarkers, markerStorey),
    // Keyed by the span: the storeys are re-derived per index revision.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allMarkers, markerStoreyKey],
  );
  const panoParam = params.get('pano');
  const requestedPano = panoParam === null ? null : Number(panoParam);
  const activePano = useMemo(
    () => (requestedPano === null ? null : ((panoList ?? []).find((p) => p.id === requestedPano) ?? null)),
    [panoList, requestedPano],
  );
  // Found among every marker, not only the storey's: a `?pano=` link to
  // another storey still opens.
  const activeMarker = useMemo(() => allMarkers.find((m) => m.id === activePano?.id) ?? null, [allMarkers, activePano]);
  // Immersive only once a placement exists: an unplaceable `?pano=` must not
  // leave the Ground in a mode it cannot render.
  const immersive = activeMarker !== null;
  const [panoramaState, setPanoramaState] = useState<PanoramaLayerState>({ loading: false, error: null });
  const [overlayGeometry, setOverlayGeometry] = useState(false);

  const enterPanorama = useCallback(
    (id: number | null) => {
      const next = new URLSearchParams(params);
      if (id === null) next.delete('pano');
      else next.set('pano', String(id));
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  // `[` / `]` and the overlay step through the storey's markers — or every
  // one when the panorama open is not on it.
  const stepList = useMemo(
    () => (activeMarker && !markers.some((m) => m.id === activeMarker.id) ? allMarkers : markers),
    [markers, allMarkers, activeMarker],
  );
  const stepBy = useCallback(
    (delta: number) => {
      const id = stepPanorama(
        stepList.map((m) => m.id),
        activeMarker?.id ?? null,
        delta,
      );
      if (id !== null) enterPanorama(id);
    },
    [stepList, activeMarker, enterPanorama],
  );

  // `Esc` leaves, `[` / `]` step (the keys `rux view` uses). Capture phase,
  // so the shell's own Esc (clear the selection) does not also fire.
  useEffect(() => {
    if (!immersive) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target && (target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"]') !== null)) return;
      if (event.key === 'Escape') enterPanorama(null);
      else if (event.key === '[') stepBy(-1);
      else if (event.key === ']') stepBy(1);
      else return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [immersive, enterPanorama, stepBy]);

  // Inside a panorama the model is hidden unless asked for; hidden, not
  // unmounted, so leaving does not re-download it.
  const geometryHidden = immersive && !overlayGeometry;
  const pointsHidden = geometryHidden || (splatWanted && splatLoaded);


  // ----------------------------------------------------------- the layer --

  // Farver: the mode's colouring (lens), RGB, or one label cloud's ids.
  // Etiket zips the chosen label cloud onto the primary layer too (first:
  // the scene's label mode colours by the first label cloud).
  const labelSources = useMemo(() => labelSourcesFor(clouds, primary), [clouds, primary]);
  const labelSource = settings.colour === 'label' ? (activeLabelSource(labelSources, settings.labelSource)?.name ?? null) : null;
  const baseLabelClouds = useMemo(() => [instCloud, roomCloud].filter((n): n is string => n !== null), [instCloud, roomCloud]);
  const colouring = useMemo(
    () => resolveColouring(settings.colour, instCloud !== null, labelSource, baseLabelClouds),
    [settings.colour, instCloud, labelSource, baseLabelClouds],
  );
  const labelClouds = colouring.labelClouds;
  const budget = densityBudget(settings.density, primary?.point_count ?? 0);
  const revision = primary
    ? labelClouds.reduce((sum, name) => sum + (cloudRevisions[name] ?? 0), cloudRevision(cloudRevisions, primary.name, null))
    : 0;

  // Lag › Punktskyer: the other renderable clouds, each off until switched
  // on (streamed with no label clouds: nothing guarantees they align).
  const extraClouds = useMemo(
    () => (clouds ?? []).filter((c) => RENDERABLE_TYPES.has(c.type) && c.name !== primary?.name && cloudOn(settings, c.name, primary?.name ?? null)),
    // `settings` is read through `clouds` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clouds, primary, settings.clouds],
  );
  const primaryOn = primary ? cloudOn(settings, primary.name, primary.name) : false;

  const layers = useMemo<ViewportLayer[]>(
    () => [
      ...(primary
        ? [
            {
              cloud: primary.name,
              visible: !pointsHidden && primaryOn,
              labelClouds,
              revision,
              overviewPoints: budget.overviewPoints,
              lodOnly: budget.lodOnly,
              identity: cloudIdentity(clouds, [primary.name, ...labelClouds]),
            },
          ]
        : []),
      ...extraClouds.map((c) => ({
        cloud: c.name,
        visible: !geometryHidden,
        revision: cloudRevision(cloudRevisions, c.name, null),
        overviewPoints: densityBudget(settings.density, c.point_count).overviewPoints,
        lodOnly: densityBudget(settings.density, c.point_count).lodOnly,
        identity: String(c.point_count),
      })),
    ],
    [primary, clouds, pointsHidden, primaryOn, labelClouds, revision, budget.overviewPoints, budget.lodOnly, extraClouds, geometryHidden, cloudRevisions, settings.density],
  );

  const [progress, setProgress] = useState<CloudStreamState | null>(null);
  const primaryName = primary?.name ?? null;
  const handleProgress = useCallback(
    (cloud: string, state: CloudStreamState) => {
      // The status line follows the primary cloud; extra layers stream quietly.
      if (cloud === primaryName) setProgress(state);
    },
    [primaryName],
  );
  const hasContent = progress !== null && (progress.loaded > 0 || (progress.overview ?? 0) > 0);

  // ------------------------------------------------------------- colour --

  const owners = useMemo(
    () => (instCloud ? instanceOwners(data.types, instCloud) : new Map<number, { typeId: number; code: string }>()),
    [data.types, instCloud],
  );
  const instancesByType = useMemo(() => {
    const out = new Map<number, number[]>();
    for (const [id, o] of owners) {
      const list = out.get(o.typeId);
      if (list) list.push(id);
      else out.set(o.typeId, [id]);
    }
    return out;
  }, [owners]);

  // Ressourcer's filters: the parts outside the set are ghost and catch no click.
  const ghostFound = useMemo(
    () => (mode === 'ressourcer' && instCloud && ghostCodes && ghostCodes.length > 0 ? partInstances(data.types, ghostCodes, instCloud) : []),
    [mode, instCloud, ghostCodes, data.types],
  );
  // Keyed by value (as `focusRooms`): a survey refresh that ghosts the same
  // instances must not rebuild the colour table and recolour every page.
  const ghostKey = ghostFound.join(',');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ghostInstances = useMemo(() => ghostFound, [ghostKey]);
  const ghostSet = useMemo(() => (ghostInstances.length > 0 ? new Set(ghostInstances) : null), [ghostInstances]);

  const colorLut = useMemo<ColorLut | null>(() => {
    if (!instCloud) return null;
    const read = tokenReader();
    const codes = sel.partCode ? [...sel.selParts, sel.partCode] : sel.selParts;
    return buildColorLut({
      cloud: instCloud,
      types: data.types.map((t) => ({
        id: t.id,
        instances: instancesByType.get(t.id) ?? [],
        color: read(modeToken(t, mode, sel.lens, data.samples)),
      })),
      focusTypeIds: focusTypes(mode, sel, data),
      selectedInstances: quiet ? [] : partInstances(data.types, codes, instCloud),
      ghostInstances,
      colors: {
        ghost: read('--color-ghost') ?? FALLBACK.ghost,
        accent: read('--color-accent') ?? FALLBACK.accent,
        neutral: read('--color-text-muted') ?? FALLBACK.neutral,
      },
      quiet,
      // A room focus keeps its one id; a storey focus keeps its rooms.
      room:
        roomCloud && focusRooms
          ? { cloud: roomCloud, keep: sel.room !== null ? sel.room : focusRooms, hide: hideOutside }
          : null,
    });
    // `sel` and `data` are read through the listed fields only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    instCloud,
    roomCloud,
    instancesByType,
    data.types,
    data.samples,
    mode,
    quiet,
    sel.lens,
    sel.typeId,
    sel.sampleId,
    sel.partCode,
    sel.selParts,
    sel.room,
    focusRooms,
    hideOutside,
    ghostInstances,
    themeTick,
  ]);

  // -------------------------------------------------------- click targets --

  const hitModel = useMemo<{ points: Float32Array; targets: HitTarget[] } | null>(() => {
    if (immersive || quiet) return null;
    if ((mode === 'ressourcer' || mode === 'gennemgang') && instCloud) {
      const ids: number[] = [];
      const targets: HitTarget[] = [];
      for (const [id, o] of owners) {
        const s = instStats.get(id);
        if (!s) continue;
        // Under a room or storey focus only the parts in its room(s) are
        // clickable: the rest are faded and should not catch a click meant
        // for the focus.
        if (focusSet && (s.room === null || !focusSet.has(s.room))) continue;
        // Nor are the parts Ressourcer's filters ghost.
        if (ghostSet?.has(id)) continue;
        ids.push(id);
        targets.push({ kind: 'part', code: o.code, typeId: o.typeId });
      }
      return ids.length > 0 ? { points: centroidsOf(instStats, ids), targets } : null;
    }
    if (mode === 'overblik' && roomStats.size > 0) {
      // Under a storey focus only its rooms are targets (a focused room keeps
      // its siblings clickable, so a click moves the focus to another room
      // on the same storey).
      const storey = storeyById(storeys, sel.storey);
      const entries = [...roomStats].filter(([id]) => !storey || storey.roomIds.includes(id));
      if (entries.length === 0) return null;
      const targets: HitTarget[] = [];
      const points = new Float32Array(entries.length * 3);
      entries.forEach(([id, s], i) => {
        points.set([s.centroid.x, s.centroid.y, s.centroid.z], i * 3);
        targets.push({ kind: 'room', id });
      });
      return { points, targets };
    }
    return null;
  }, [immersive, quiet, mode, instCloud, owners, instStats, roomStats, storeys, sel.storey, focusSet, ghostSet]);

  const selRef = useRef(sel);
  selRef.current = sel;
  const handleClick = useCallback(
    (targets: HitTarget[], i: number | null, shift: boolean) => {
      if (i === null) return;
      const target = targets[i];
      if (!target) return;
      const s = selRef.current;
      if (target.kind === 'room') {
        setSel({ room: s.room === target.id ? null : target.id });
        return;
      }
      if (s.typeId === target.typeId) setSel({ selParts: togglePart(s.selParts, target.code) });
      else if (shift) setSel({ typeId: target.typeId, selParts: togglePart(s.selParts, target.code) });
      else setSel({ typeId: target.typeId });
    },
    [setSel],
  );

  const pointerHits = useMemo<PointerHits | undefined>(
    () =>
      hitModel
        ? { points: hitModel.points, onHover: NOOP, onClick: (i, shift) => handleClick(hitModel.targets, i, shift) }
        : undefined,
    [hitModel, handleClick],
  );

  // --------------------------------------------------------- room labels --

  const roomLabels = useMemo<RoomLabel[] | undefined>(() => {
    if (!settings.roomLabels || quiet || immersive || !roomCloud || roomStats.size === 0) return undefined;
    // The plan names the building model's spaces instead, when it has any.
    if (planLock && (spatial.spaces?.length ?? 0) > 0) return undefined;
    const out: RoomLabel[] = [];
    for (const [id, s] of roomStats) {
      // The hidden rooms' labels go with their points.
      if (hideOutside && focusSet !== null && !focusSet.has(id)) continue;
      out.push({
        key: `r${id}`,
        name: roomLabelName(id),
        world: [s.centroid.x, s.centroid.y, s.centroid.z],
        dim: focusSet !== null && !focusSet.has(id),
      });
    }
    return out;
  }, [settings.roomLabels, quiet, immersive, roomCloud, roomStats, roomLabelName, focusSet, hideOutside, planLock, spatial.spaces]);

  // A room or storey focus frames its room(s) (once per focus, as soon as
  // the extent is known — a storey: the union of its rooms' boxes); clearing
  // it frames the whole building again.
  const framedFocus = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const handle = viewportRef.current;
    if (!handle) return;
    if (focusKey === null || focusRooms === null) {
      if (framedFocus.current !== undefined && framedFocus.current !== null) handle.frameAll();
      framedFocus.current = null;
      return;
    }
    if (framedFocus.current === focusKey) return;
    const min = { x: Infinity, y: Infinity, z: Infinity };
    const max = { x: -Infinity, y: -Infinity, z: -Infinity };
    let found = false;
    for (const id of focusRooms) {
      const box = roomStats.get(id);
      if (!box) continue;
      found = true;
      min.x = Math.min(min.x, box.min.x);
      min.y = Math.min(min.y, box.min.y);
      min.z = Math.min(min.z, box.min.z);
      max.x = Math.max(max.x, box.max.x);
      max.y = Math.max(max.y, box.max.y);
      max.z = Math.max(max.z, box.max.z);
    }
    if (!found) {
      // A storey with no room boxes (an imported model's, or before the
      // stream lands): frame its height band across the whole model.
      const origin = handle.worldOrigin();
      const bounds = handle.sceneBounds();
      if (sel.room !== null || !focusStorey || !origin || !bounds) return; // try again on the next revision
      handle.frameWorldBox(
        { x: bounds.min.x + origin[0], y: bounds.min.y + origin[1], z: focusStorey.zMin },
        { x: bounds.max.x + origin[0], y: bounds.max.y + origin[1], z: focusStorey.zMax },
      );
      framedFocus.current = focusKey;
      return;
    }
    handle.frameWorldBox(min, max);
    framedFocus.current = focusKey;
    // `focusStorey` is read through `focusKey`; `meshContent` retries once a model mesh has bounds.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey, focusRooms, roomStats, meshContent]);

  // -------------------------------------------------------------- facts --

  useEffect(() => {
    publish({ clouds: cloudsRead.data ?? (cloudsRead.error ? [] : null), primary: primaryName });
  }, [publish, cloudsRead.data, cloudsRead.error, primaryName]);
  useEffect(() => {
    publish({ meshes: meshesRead.data ?? (meshesRead.error ? [] : null) });
  }, [publish, meshesRead.data, meshesRead.error]);
  useEffect(() => {
    publish({ gsplats: gsplatsRead.data ?? (gsplatsRead.error ? [] : null) });
  }, [publish, gsplatsRead.data, gsplatsRead.error]);
  useEffect(() => {
    publish({ panoramas: panoList ? allMarkers.length : panoramasRead.error ? 0 : null });
  }, [publish, panoList, panoramasRead.error, allMarkers.length]);
  useEffect(() => {
    publish({ poseNodes: poseRead.data ? poseRead.data.nodes.length : null });
  }, [publish, poseRead.data]);
  useEffect(() => {
    const origin = viewportRef.current?.worldOrigin() ?? null;
    publish({
      instances: instStats.size,
      bounds: viewportRef.current?.sceneBounds() ?? null,
      worldOrigin: origin ? { x: origin[0], y: origin[1], z: origin[2] } : null,
    });
  }, [publish, instStats, indexRev]);

  // Lag › Mesh reports the scan mesh's download.
  const scanMeshName = mesh?.name ?? null;
  const handleMeshProgress = useCallback(
    (name: string, state: MeshLayerState) => {
      if (!state.loading && !state.error) setMeshContent(true);
      // An own import: frame the model once, when the last of its categories has landed.
      const waiting = frameAfter.current;
      if (waiting && !state.loading && waiting.delete(name) && waiting.size === 0) {
        frameAfter.current = null;
        viewportRef.current?.frameAll();
      }
      if (name !== scanMeshName) {
        const load: LayerLoad = state.loading ? 'loading' : state.error ? 'error' : 'loaded';
        setModelLoads((prev) => (prev[name] === load ? prev : { ...prev, [name]: load }));
      }
      if (name === scanMeshName) publish({ meshLoading: state.loading, meshError: state.error?.message ?? null });
    },
    [publish, scanMeshName],
  );
  const handleSplatProgress = useCallback(
    (_name: string, state: SplatLayerState) => {
      setSplatLoaded(state.loaded);
      publish({
        // Rounded so a fast download does not publish (and re-render the panel) per chunk.
        splatProgress: state.loaded ? 1 : state.loading ? Math.round((state.fraction ?? 0) * 20) / 20 : null,
        splatError: state.error?.message ?? null,
      });
    },
    [publish],
  );

  // -------------------------------------------------------- mesh, splat --

  // Låst plan with a storey in focus: cut the model at the plan height above
  // the storey's floor (`planCutHeight`), so the storeys above and this
  // one's ceiling do not cover the plan. Not inside a panorama — the cut
  // would slice the sphere the image is drawn on.
  // The plan cuts the Etage storey (Geometri), else the focused one (frontend-r22.27).
  const plan = useMemo(() => planScope(elementFilter.storey, storeys, markerStorey?.id ?? null), [elementFilter.storey, storeys, markerStorey]);
  const sectionHeight = planLock && !immersive ? plan.cutZ : null;

  const meshes = useMemo<ViewportMesh[]>(
    () => [
      ...(mesh && meshRequested
        ? [{ name: mesh.name, url: api.meshDataUrl(mesh.name), visible: settings.mesh && !geometryHidden, wireframe: settings.wireframe, identity: meshIdentity(mesh) }]
        : []),
      ...model
        .filter((l) => modelRequested.has(l.name))
        .map((l) => ({
          name: l.name,
          url: api.meshDataUrl(l.name),
          visible: modelLayerVisible(l) && !geometryHidden,
          wireframe: settings.wireframe,
          colorToken: kindColorToken(l.kind),
          caps: sectionHeight !== null,
          revision: meshRevs[l.name] ?? 0,
          identity: meshIdentity(l.info),
        })),
    ],
    [mesh, meshRequested, settings.mesh, settings.wireframe, geometryHidden, model, modelRequested, modelLayerVisible, sectionHeight, meshRevs],
  );

  // A case whose only geometry is the scan's mesh shows it rather than an
  // empty canvas (once; the switch stays the user's after that).
  const meshDefaulted = useRef(false);
  useEffect(() => {
    if (meshDefaulted.current || cloudsRead.data === undefined || !mesh) return;
    meshDefaulted.current = true;
    if (!primary && model.length === 0 && !hasElementRows && !settings.mesh) dispatch({ type: 'set', patch: { mesh: true } });
  }, [cloudsRead.data, mesh, primary, model.length, hasElementRows, settings.mesh, dispatch]);

  const splats = useMemo<ViewportSplat[]>(
    () =>
      splat && splatRequested
        ? [{ name: splat.name, url: api.gsplatDataUrl(splat.name), visible: splatWanted && !geometryHidden }]
        : [],
    [splat, splatRequested, splatWanted, geometryHidden],
  );

  // -------------------------------------------------------- camera, view --

  // A preset view from inside a panorama is a request to leave it (the old
  // viewport's reading of Frame all / a preset press).
  const viewNonce = settings.view?.nonce ?? 0;
  const seenView = useRef(viewNonce);
  useEffect(() => {
    if (seenView.current === viewNonce) return;
    seenView.current = viewNonce;
    if (immersive) enterPanorama(null);
    // Only the nonce matters here: a new request, not a re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewNonce]);

  // ------------------------------------------------- Vælg punkt (#454) --

  // The tool lives on the Ground: on, the next click on the model picks a
  // point instead of a part or room, and the frames that see it are listed.
  // Not while a centre tile (Tabel, Galleri, evidence) covers the model: the tool would sit under it.
  const centreCovers = sel.ev !== null || (mode === 'ressourcer' && sel.view !== 'model');
  const pickAvailable = !immersive && !quiet && !centreCovers && primary !== null;
  const picking = settings.picking && pickAvailable;
  const setPicking = useCallback((on: boolean) => dispatch({ type: 'set', patch: { picking: on } }), [dispatch]);
  const [picked, setPicked] = useState<WorldPoint | null>(null);
  const [visibility, setVisibility] = useState<FrameVisibilityList | null>(null);
  const [visibilityError, setVisibilityError] = useState<Error | null>(null);
  const visibilityAbort = useRef<AbortController | null>(null);
  useEffect(() => () => visibilityAbort.current?.abort(), []);

  const handlePickPoint = useCallback(
    (point: WorldPoint) => {
      visibilityAbort.current?.abort();
      const controller = new AbortController();
      visibilityAbort.current = controller;
      setPicked(point);
      setVisibility(null);
      setVisibilityError(null);
      setPicking(false);
      api
        .pointVisibility(point.x, point.y, point.z, { limit: 12 }, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) setVisibility(result);
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) setVisibilityError(error instanceof Error ? error : new Error(String(error)));
        });
    },
    [setPicking],
  );
  const closeSource = useCallback(() => {
    visibilityAbort.current?.abort();
    setPicked(null);
    setVisibility(null);
    setVisibilityError(null);
  }, []);
  // A frame opens in Segmentering with the point seeded where it projects.
  const openFrame = useCallback((frame: VisibleFrame) => navigate(segmentHref(frame.frame_id, { u: frame.u, v: frame.v })), [navigate]);

  // Esc cancels the pick (capture phase, before the shell's own Esc).
  useEffect(() => {
    if (!picking) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      setPicking(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [picking, setPicking]);
  // Leaving the tool's reach (a panorama, Rapport) drops a pending pick.
  useEffect(() => {
    if (!pickAvailable && settings.picking) setPicking(false);
  }, [pickAvailable, settings.picking, setPicking]);

  // ------------------------------------------------ Klippeboks (#444) --

  // Two switches on the Ground's toolbar: Klippeboks shows the box and its
  // face handles in the model (drag one to cut), Klip turns the cut on and
  // off. The tool needs the model's bounds (content loaded) and is hidden
  // where Vælg punkt is; the cut itself stays wherever the model is drawn.
  const clipAvailable = !immersive && !quiet && !centreCovers && (hasContent || splatLoaded || meshContent || elementsShown);
  // Låst plan: wherever the model is shown and not covered.
  const planAvailable = !immersive && !quiet && !centreCovers;
  const setPlanLock = useCallback((on: boolean) => dispatch({ type: 'set', patch: { planLock: on } }), [dispatch]);
  const clipToolShown = settings.clipTool && clipAvailable;
  // Klip means something once there is a box, or the tool to draw one.
  const clipUsable = settings.clipTool || settings.clip !== null;
  const clipping = useMemo(
    () => ({
      toolVisible: clipToolShown,
      active: settings.clipOn,
      min: settings.clip?.min ?? null,
      max: settings.clip?.max ?? null,
      onBoxChange: (min: WorldPoint, max: WorldPoint) => dispatch({ type: 'clip', box: { min, max } }),
    }),
    [clipToolShown, settings.clipOn, settings.clip, dispatch],
  );

  const pointSize = pointSizeOf(settings);


  // ------------------------------- the plan: spaces, Placér 360° (ayw.3/.8) --

  // Drawn over the locked plan wherever the model shows.
  const planDrawn = planLock && planAvailable;
  const planStoreyId = plan.storeyId;
  // What the plan shows by storey: Etage's own filter, else just the focused storey.
  const planFilter = useMemo<ElementFilterState>(
    () => ({
      ...elementFilter,
      storey: elementFilter.storey.storeyId !== null ? elementFilter.storey : plan.storeyId !== null ? { storeyId: plan.storeyId, andBelow: false } : INITIAL_STOREY_FILTER,
    }),
    [elementFilter, plan.storeyId],
  );
  // Bygningsdele (frontend-t02): kept to what the category/storey filter shows.
  const filteredSpaces = useMemo(() => visibleSpaces(spatial.spaces, elementFilter), [spatial.spaces, elementFilter]);
  const planSpaceSet = useMemo(
    () => (planDrawn ? planSpaces(filteredSpaces, planStoreyId) : { shown: [], ghost: [] }),
    [planDrawn, filteredSpaces, planStoreyId],
  );
  const filteredSlabs = useMemo(() => visibleSlabs(spatial.slabs, elementFilter), [spatial.slabs, elementFilter]);
  const planSlabSet = useMemo(
    () => (planDrawn ? planSlabs(filteredSlabs, planStoreyId) : []),
    [planDrawn, filteredSlabs, planStoreyId],
  );
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);
  useEffect(() => setSelectedSpace(null), [planStoreyId, planLock]);

  // Placér 360°: editors, in the plan.
  const placeAvailable = canEdit && planDrawn;
  // Unlocking the plan (or losing the editor role) ends it; a trip into a
  // panorama (Åbn) or under a centre tile only hides it.
  const placeKept = canEdit && planLock;
  const uploadAbort = useRef<AbortController | null>(null);
  useEffect(() => () => uploadAbort.current?.abort(), []);
  const placingRef = useRef(placing);
  placingRef.current = placing;
  /** Close the tool: the drag, an upload in flight and the error go with it. */
  const closePlacing = useCallback(() => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    setPlacing(null);
    setDragPreview(null);
    setDragCancel((n) => n + 1);
    setPlaceError(null);
  }, []);
  useEffect(() => {
    if (placeKept || placing === null) return;
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    setPlacing(null);
    setDragPreview(null);
    setPlaceError(null);
  }, [placeKept, placing]);
  const placingOn = placeAvailable && placing !== null;
  // The floor a new panorama stands on: the focused storey's, else the lowest.
  const floorZ = markerStorey?.zMin ?? storeys[0]?.zMin ?? planSpaceSet.shown[0]?.z_floor ?? null;

  // ------------------- the building model's walls; Mål afstand / areal (aa2, 4rt) --

  useEffect(() => {
    publish({ walls: building.walls ? { walls: building.walls.length, openings: building.openings?.length ?? 0 } : null });
  }, [publish, building.walls, building.openings]);

  // Bygningsdele (frontend-t02): every category's tallies, for the panel's
  // rows and counts, and the walls/openings/spaces kept to what the filter
  // shows, for the plan overlay below. `solid_slabs`' element index (slab vs
  // roof) has no dedicated endpoint, so it is read from the mesh list.
  const wallGuidStoreys = useMemo(() => wallStoreysByGuid(building.walls), [building.walls]);
  const elementTallies = useMemo(
    () => buildingElementTallies({ walls: building.walls, openings: building.openings, spaces: spatial.spaces, slabs: spatial.slabs }),
    [building.walls, building.openings, spatial.spaces, spatial.slabs],
  );
  useEffect(() => {
    publish({ buildingElementTallies: elementTallies });
  }, [publish, elementTallies]);
  const filteredWalls = useMemo(() => visibleWalls(building.walls, planFilter), [building.walls, planFilter]);
  const filteredOpenings = useMemo(
    () => visibleOpenings(building.openings, wallGuidStoreys, planFilter),
    [building.openings, wallGuidStoreys, planFilter],
  );

  // Drawn on the locked plan by drawing conventions (poché where the cut goes through), under the spaces' names and the markers.
  const wallSet = useMemo(
    () => (planDrawn && building.walls ? planWallSet(filteredWalls, filteredOpenings, planStoreyId, plan.cutZ) : null),
    [planDrawn, building.walls, filteredWalls, filteredOpenings, planStoreyId, plan.cutZ],
  );

  // ------------------- the building model in 3D; Etage as the clipping box (r22.27) --

  // Every element as its own solid, in place of the reconstruction mesh;
  // the category toggles hide them, the storey is the clipping box's job.
  const elementList = useBuildingElements({ walls: building.walls, openings: building.openings, spaces: spatial.spaces, slabs: spatial.slabs });
  // Rediger › Geometri shares one selection between its tree, the plan and
  // the 3D model: the URL's `?el=` (frontend-u0o.2). Elsewhere the pick is
  // the Ground's own.
  const rediger = mode === 'rediger';
  const [localElement, setLocalElement] = useState<string | null>(null);
  const selectedElement = rediger ? sel.element : localElement;
  const setSelectedElement = useCallback(
    (guid: string | null) => (rediger ? setSel({ element: guid }) : setLocalElement(guid)),
    [rediger, setSel],
  );
  // A category an imported Revit/IFC model has a layer of is drawn by that
  // layer (its own, full-fidelity mesh, under the same toggle): not twice.
  const elementHidden = useMemo(() => {
    const hidden = new Set(elementFilter.hidden);
    for (const l of model) {
      const category = elementCategoryForImportedKind(l.kind);
      if (category) hidden.add(category);
    }
    return hidden;
  }, [elementFilter.hidden, model]);
  const drawnElements = useMemo(() => elementsDrawn(elementList, elementHidden), [elementList, elementHidden]);
  const viewportElements = useMemo<ViewportElements | null>(
    () =>
      elementList && elementList.length > 0
        ? {
            list: elementList,
            hidden: elementHidden,
            // The plan draws them by its own conventions (PlanWalls, PlanLayer).
            visible: !geometryHidden && !planDrawn,
            wireframe: settings.wireframe,
            selected: selectedElement,
          }
        : null,
    [elementList, elementHidden, geometryHidden, planDrawn, settings.wireframe, selectedElement],
  );
  const pickedElement = useMemo(
    () => (selectedElement ? (drawnElements.find((e) => e.guid === selectedElement) ?? null) : null),
    [drawnElements, selectedElement],
  );
  useEffect(() => {
    // Rediger's selection may name an element the 3D model does not draw (the plan's, a filtered category).
    if (!rediger && localElement !== null && !pickedElement) setLocalElement(null);
  }, [rediger, localElement, pickedElement]);
  const handlePickElement = useCallback(
    (guid: string | null) => setSelectedElement(guid === selectedElement ? null : guid),
    [selectedElement, setSelectedElement],
  );
  // Rediger › Geometri's zoom to an element or a warning (`focus`, a one-shot request).
  const focusNonce = settings.focusBox?.nonce ?? 0;
  const focusSeen = useRef(focusNonce);
  useEffect(() => {
    if (focusNonce === focusSeen.current) return;
    focusSeen.current = focusNonce;
    const box = settings.focusBox;
    if (box) viewportRef.current?.frameWorldBox(box.min, box.max);
  }, [focusNonce, settings.focusBox]);

  // Etage (Geometri) sets the clipping box's heights: Kun etage N from its
  // floor slab to under the next floor, Etage N og under everything up to
  // there. Followed as the model's bounds grow; a box the user has dragged
  // since is theirs until the storey changes, and "Alle etager" takes the
  // storey's box away again.
  const levels = useMemo(() => storeyLevels(storeys, spatial.slabs), [storeys, spatial.slabs]);
  const storeyKey = `${elementFilter.storey.storeyId}|${elementFilter.storey.andBelow}`;
  const autoClip = useRef<{ key: string; box: ClipBox } | null>(null);
  const contentTick = indexRev + (elementsShown ? 1e9 : 0) + (meshContent ? 2e9 : 0) + (splatLoaded ? 4e9 : 0);
  useEffect(() => {
    const range = storeyZRange(levels, elementFilter.storey.storeyId, elementFilter.storey.andBelow);
    const prev = autoClip.current;
    if (range === null) {
      if (prev) {
        autoClip.current = null;
        if (sameBox(settings.clip, prev.box)) dispatch({ type: 'clip', box: null });
      }
      return;
    }
    const handle = viewportRef.current;
    const origin = handle?.worldOrigin();
    const bounds = handle?.sceneBounds();
    if (!origin || !bounds) return;
    const box = clipForZRange(bounds, { x: origin[0], y: origin[1], z: origin[2] }, range);
    if (!box) return;
    if (prev && prev.key === storeyKey && (!sameBox(settings.clip, prev.box) || sameBox(prev.box, box))) return;
    autoClip.current = { key: storeyKey, box };
    dispatch({ type: 'clip', box });
    if (!settings.clipOn) dispatch({ type: 'set', patch: { clipOn: true } });
  }, [storeyKey, levels, elementFilter.storey, contentTick, settings.clip, settings.clipOn, dispatch]);
  // The plan's floor for measuring: the storey's, else the model's lowest.
  const sceneFloor = (() => {
    const origin = viewportRef.current?.worldOrigin();
    const bounds = viewportRef.current?.sceneBounds();
    return origin && bounds ? bounds.min.z + origin[2] : 0;
  })();
  const measurePlanZ = floorZ ?? sceneFloor;
  // Snap features for this view: laid flat on the plan's floor, or at their heights in 3D.
  const snapFeatures = useMemo(
    () =>
      building.walls
        ? bimFeatures(building.walls, building.openings ?? [], {
            planZ: planLock ? measurePlanZ : null,
            storeyId: planLock ? planStoreyId : null,
          })
        : [],
    [building.walls, building.openings, planLock, measurePlanZ, planStoreyId],
  );
  // Read-only: a viewer measures too. Wherever the clipping tools are (a model on screen, nothing over it).
  const measure = useMeasureTool({
    viewport: viewportRef,
    host: groundRef,
    available: clipAvailable,
    plan: planLock,
    planZ: measurePlanZ,
    features: snapFeatures,
  });
  const measuring = measure.active;
  const setMeasureTool = measure.setTool;

  /**
   * Save a marker's new position / heading (only what changed), shown ahead
   * of the answer. Only the latest save of a marker is applied when it
   * answers, or rolled back when it fails: an older answer arriving late
   * would put the marker back two edits ago (`beginSave` / `settleSave`).
   */
  const commitPlacement = useCallback(
    async (id: number, to: { x?: number; y?: number; z?: number; yaw?: number | null }) => {
      const pano = panoById.get(id);
      const placement = pano ? resolvePlacement(pano) : null;
      if (!pano || !placement) return;
      const from = { x: placement.position[0], y: placement.position[1], z: placement.position[2], yaw: panoramaYaw(pano) };
      const nudge = moveNudge(from, { x: to.x ?? from.x, y: to.y ?? from.y, z: to.z ?? from.z, yaw: to.yaw === undefined ? from.yaw : to.yaw });
      if (!nudge) return;
      const optimistic = optimisticPlacement(pano, nudge);
      const begun = beginSave(saveTracker.current, id, optimistic);
      saveTracker.current = begun.tracker;
      setSaveTick((n) => n + 1);
      if (optimistic) setPanoList((list) => upsertPanorama(list ?? [], optimistic));
      setPlaceError(null);
      let saved: PanoramaInfo | null = null;
      let failure: unknown = null;
      try {
        saved = await api.placePanorama(id, nudge);
      } catch (error) {
        failure = error;
      }
      const settled = settleSave(saveTracker.current, id, begun.seq);
      saveTracker.current = settled.tracker;
      setSaveTick((n) => n + 1);
      if (!settled.latest) return; // a newer save of this marker decides
      if (saved) {
        const answer = saved;
        setPanoList((list) => upsertPanorama(list ?? [], answer));
      } else {
        // Back to before this edit, then to the server's own word.
        setPanoList((list) => upsertPanorama(list ?? [], pano));
        setPlaceError(placementErrorText(failure, 'move'));
        reloadPanoramas();
      }
    },
    [panoById, reloadPanoramas],
  );

  const { snapPlanXY, setHover: clearSnapHover } = measure;
  const handleDrag = useCallback(
    (kind: PlanDragKind, id: number, world: [number, number], shift: boolean) => {
      const pano = panoById.get(id);
      const placement = pano ? resolvePlacement(pano) : null;
      if (!pano || !placement) return;
      setDragPreview((prev) => {
        const base: DragPreview =
          prev && prev.id === id ? prev : { id, x: placement.position[0], y: placement.position[1], yaw: panoramaYaw(pano) };
        if (kind === 'move') {
          const [x, y] = snapPlanXY(world, true);
          return { ...base, x, y };
        }
        const yaw = yawFromDrag([base.x, base.y], world);
        if (yaw === null) return base;
        return { ...base, yaw: shift ? snapYaw(yaw) : Math.round(yaw * 10) / 10 };
      });
    },
    [panoById, snapPlanXY],
  );
  const handleDragEnd = useCallback(() => {
    clearSnapHover(null);
    const preview = dragPreviewRef.current;
    if (!preview) return; // cancelled with Esc
    setDragPreview(null);
    void commitPlacement(preview.id, { x: preview.x, y: preview.y, yaw: preview.yaw });
  }, [commitPlacement, clearSnapHover]);
  const handleDragCancel = useCallback(() => {
    clearSnapHover(null);
    setDragPreview(null);
  }, [clearSnapHover]);

  /** The floor (world z) a height is measured from: the storey the point is on; null without storeys. */
  const floorOf = useCallback((z: number): number | null => storeys.find((st) => onStorey(z, st))?.zMin ?? null, [storeys]);

  // Tilføj 360°-billede: a JPEG, checked here (type, size, 2:1 from its header) before anything is sent.
  const handlePickFile = useCallback(
    async (file: File) => {
      setPlaceError(null);
      const problem = fileProblem(file);
      if (problem) {
        setPlaceError(problem);
        return;
      }
      let size: { width: number; height: number } | null = null;
      try {
        size = jpegSize(new Uint8Array(await file.slice(0, 2 * 1024 * 1024).arrayBuffer()));
        if (!size && typeof createImageBitmap === 'function') {
          const bitmap = await createImageBitmap(file);
          size = { width: bitmap.width, height: bitmap.height };
          bitmap.close();
        }
      } catch {
        size = null;
      }
      const dims = size ? equirectProblem(size.width, size.height) : 'Billedet kunne ikke læses som JPEG.';
      if (dims || !size) {
        setPlaceError(dims);
        return;
      }
      setPlacing({
        kind: 'add',
        file,
        detail: `${size.width.toLocaleString('da-DK')} × ${size.height.toLocaleString('da-DK')} px · ${formatMb(file.size)}`,
        at: null,
        z: Math.round(defaultCameraZ(floorZ) * 100) / 100,
        yaw: 0,
        progress: null,
      });
    },
    [floorZ],
  );

  const submitAdd = useCallback(async () => {
    if (placing?.kind !== 'add' || !placing.at || placing.progress !== null) return;
    const built = uploadQuery({ x: placing.at[0], y: placing.at[1], z: placing.z, yawDeg: placing.yaw, filename: placing.file.name });
    if ('error' in built) {
      setPlaceError(built.error);
      return;
    }
    setPlaceError(null);
    setPlacing({ ...placing, progress: 0 });
    const controller = new AbortController();
    uploadAbort.current = controller;
    try {
      const created = await api.uploadPanorama(placing.file, built.query, {
        signal: controller.signal,
        onProgress: (fraction) => setPlacing((p) => (p?.kind === 'add' ? { ...p, progress: fraction } : p)),
      });
      // It is on the server whatever happened here since: list it.
      setPanoList((list) => upsertPanorama(list ?? [], created));
      if (uploadAbort.current !== controller) return; // cancelled, or the tool closed: leave the tool alone
      uploadAbort.current = null;
      if (placingRef.current?.kind === 'add') setPlacing({ kind: 'select', id: created.id, moving: false });
    } catch (error) {
      if (controller.signal.aborted || uploadAbort.current !== controller) return;
      uploadAbort.current = null;
      setPlaceError(placementErrorText(error, 'add'));
      setPlacing((p) => (p?.kind === 'add' ? { ...p, progress: null } : p));
    }
  }, [placing]);

  /** Annullér upload: stop sending; the add stays open to try again. */
  const cancelUpload = useCallback(() => {
    uploadAbort.current?.abort();
    uploadAbort.current = null;
    setPlacing((p) => (p?.kind === 'add' ? { ...p, progress: null } : p));
    setPlaceError('Uploaden blev annulleret.');
  }, []);

  // A marker click while placing picks it rather than stepping into it.
  const handlePickPanorama = useCallback(
    (id: number) => {
      if (placingOn && placing?.kind === 'add') return;
      if (placingOn && placing?.kind === 'select') {
        if (placing.id !== id) setPlacing({ kind: 'select', id, moving: false });
        setPlaceError(null);
        return;
      }
      enterPanorama(id);
    },
    [placingOn, placing, enterPanorama],
  );

  /** Whether canvas pixel (x, y) is on one of the shown markers. */
  const nearMarker = useCallback(
    (x: number, y: number): boolean => {
      const handle = viewportRef.current;
      if (!handle || !settings.panoramas || markers.length === 0) return false;
      const world = new Float64Array(markers.length * 3);
      markers.forEach((m, i) => world.set(m.placement.position, i * 3));
      const screen = handle.projectWorld(world, { clip: false });
      for (let i = 0; i < markers.length; i += 1) {
        if (Math.hypot(screen[i * 2] - x, screen[i * 2 + 1] - y) <= MARKER_CLICK_PX) return true;
      }
      return false;
    },
    [markers, settings.panoramas],
  );

  // Clicks on the plan: place a new panorama, drop the marker selection, pick a space.
  const handleCanvasClick = useCallback(
    (x: number, y: number): boolean => {
      const handle = viewportRef.current;
      if (!handle) return false;
      if (placingOn && placing?.kind === 'add') {
        if (placing.progress !== null) return true;
        const hit = handle.planPoint(x, y, placing.z);
        if (hit) {
          setPlacing({ ...placing, at: measure.snapPlanXY([hit[0], hit[1]], false) });
          setPlaceError(null);
        }
        return true;
      }
      if (nearMarker(x, y)) return false; // the marker's (Viewport picks it next)
      if (placingOn && placing?.kind === 'select' && placing.id !== null) {
        if (!placing.moving) setPlacing({ kind: 'select', id: null, moving: false });
        return true;
      }
      if (mode === 'rediger') {
        // Rediger › Geometri: a wall or an opening (8 px of slack), else a space, else nothing.
        const z = floorZ ?? plan.cutZ ?? 0;
        const hit = handle.planPoint(x, y, z);
        if (!hit) return false;
        const near = handle.planPoint(x + 8, y, z);
        const tol = near ? Math.hypot(near[0] - hit[0], near[1] - hit[1]) : 0.1;
        const walls = planStoreyId === null ? filteredWalls : filteredWalls.filter((w) => w.storey_id === planStoreyId);
        const guid =
          pickPlanElement(walls, filteredOpenings, [hit[0], hit[1]], tol) ?? spaceAt(planSpaceSet.shown, hit[0], hit[1])?.guid ?? null;
        setSel({ element: guid === selRef.current.element ? null : guid });
        return true;
      }
      const shown = planSpaceSet.shown;
      if (shown.length === 0) return false;
      const hit = handle.planPoint(x, y, floorZ ?? shown[0].z_floor);
      const space = hit ? spaceAt(shown, hit[0], hit[1]) : null;
      if (!space) {
        if (selectedSpace === null) return false;
        setSelectedSpace(null);
        return true;
      }
      // A space that is a room of the rooms cloud focuses it, as its centroid would in Overblik.
      if (mode === 'overblik' && space.room_label !== null && roomStats.has(space.room_label)) {
        setSel({ room: selRef.current.room === space.room_label ? null : space.room_label });
      }
      setSelectedSpace((cur) => (cur === space.guid ? null : space.guid));
      return true;
    },
    [placingOn, placing, nearMarker, planSpaceSet.shown, floorZ, selectedSpace, mode, roomStats, setSel, measure.snapPlanXY, plan.cutZ, planStoreyId, filteredWalls, filteredOpenings],
  );

  // Esc while placing undoes the innermost thing (`escapeAction`): the
  // drag, the upload, the add, Flyt, the pick, then the tool. The arrow keys
  // nudge the picked marker 0.1 m (Shift: 1 m) through the same save.
  useEffect(() => {
    if (!placingOn || !placing) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      // A field resets itself; a dialog or menu has its own Esc.
      if (target && target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]') !== null) return;
      if (event.key === 'Escape') {
        const action = escapeAction(dragPreviewRef.current !== null, {
          ...(placing.kind === 'add' ? { kind: 'add' as const, uploading: placing.progress !== null } : placing),
        });
        if (action === null) return;
        event.preventDefault();
        event.stopPropagation();
        if (action === 'cancel-drag') {
          setDragPreview(null);
          setDragCancel((n) => n + 1);
        } else if (action === 'cancel-upload') cancelUpload();
        else if (action === 'cancel-add' || action === 'deselect') {
          setPlacing({ kind: 'select', id: null, moving: false });
          setPlaceError(null);
        } else if (action === 'stop-move' && placing.kind === 'select') setPlacing({ ...placing, moving: false });
        else if (action === 'close') closePlacing();
        return;
      }
      const step = event.shiftKey ? 1 : 0.1;
      const delta: Record<string, [number, number]> = {
        ArrowLeft: [-step, 0],
        ArrowRight: [step, 0],
        ArrowUp: [0, step],
        ArrowDown: [0, -step],
      };
      const d = delta[event.key];
      if (!d || placing.kind !== 'select' || placing.id === null || dragPreviewRef.current) return;
      const pano = panoById.get(placing.id);
      const at = pano ? resolvePlacement(pano) : null;
      if (!at) return;
      event.preventDefault();
      event.stopPropagation();
      // The plan has +y up the screen: ↑ is +y.
      void commitPlacement(placing.id, { x: at.position[0] + d[0], y: at.position[1] + d[1] });
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [placingOn, placing, cancelUpload, closePlacing, panoById, commitPlacement]);

  // The plan layer's marker chrome: manual markers carry their heading;
  // while placing, every marker whose heading is known, and the pick.
  const selectedPano = placingOn && placing?.kind === 'select' ? placing.id : null;
  const planMarkers = useMemo<PlanMarker[]>(() => {
    if (!planDrawn || !settings.panoramas) return [];
    const out: PlanMarker[] = [];
    for (const m of markers) {
      const pano = panoById.get(m.id);
      if (!pano) continue;
      const manual = pano.pose_source === 'manual';
      const selected = m.id === selectedPano;
      if (!manual && !selected && !placingOn) continue;
      const yaw = dragPreview?.id === m.id && dragPreview.yaw !== null ? dragPreview.yaw : panoramaYaw(pano);
      const [x, y, z] = m.placement.position;
      out.push({ id: m.id, x, y, z, yaw, arrow: manual || selected || m.placement.heading === 'resected', selected });
    }
    if (placingOn && placing?.kind === 'add' && placing.at) {
      out.push({ id: -1, x: placing.at[0], y: placing.at[1], z: placing.z, yaw: placing.yaw, arrow: true, selected: false, pending: true });
    }
    return out;
  }, [planDrawn, settings.panoramas, markers, panoById, selectedPano, placingOn, placing, dragPreview]);

  const placementView = useMemo<PlacementView>(() => {
    if (!placing) return { kind: 'idle' };
    if (placing.kind === 'add') {
      const floor = floorZ;
      return {
        kind: 'add',
        filename: placing.file.name,
        detail: placing.detail,
        placed: placing.at !== null,
        height: Math.round((placing.z - (floor ?? 0)) * 100) / 100,
        heightLabel: floor === null ? 'Højde (z)' : 'Højde over gulv',
        yaw: placing.yaw,
        progress: placing.progress,
      };
    }
    const pano = placing.id !== null ? panoById.get(placing.id) : undefined;
    const placement = pano ? resolvePlacement(pano) : null;
    if (!pano || !placement) return { kind: 'idle' };
    const [x, y, z] = placement.position;
    const floor = floorOf(z);
    // A drag in progress shows its live position and heading.
    const live = dragPreview?.id === pano.id ? dragPreview : null;
    return {
      kind: 'selected',
      id: pano.id,
      filename: pano.filename,
      source: describePlacementDa(pano, placement),
      x: Math.round((live?.x ?? x) * 1000) / 1000,
      y: Math.round((live?.y ?? y) * 1000) / 1000,
      yaw: live && live.yaw !== null ? live.yaw : panoramaYaw(pano),
      height: Math.round((z - (floor ?? 0)) * 100) / 100,
      heightLabel: floor === null ? 'Højde (z)' : 'Højde over gulv',
      moving: placing.moving,
      saving: isSaving(saveTracker.current, pano.id),
    };
    // `saveTick` re-reads the tracker.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placing, panoById, saveTick, floorZ, floorOf, dragPreview]);

  // The panel shows heights over the storey floor; the server stores world z.
  const handlePanelCommit = useCallback(
    (field: PlacementField, value: number) => {
      if (placing?.kind === 'add') {
        if (field === 'yaw') setPlacing({ ...placing, yaw: value });
        else if (field === 'height') setPlacing({ ...placing, z: (floorZ ?? 0) + value });
        return;
      }
      if (placing?.kind !== 'select' || placing.id === null) return;
      const pano = panoById.get(placing.id);
      const at = pano ? resolvePlacement(pano) : null;
      if (!at) return;
      if (field === 'yaw') void commitPlacement(placing.id, { yaw: value });
      else if (field === 'x') void commitPlacement(placing.id, { x: value });
      else if (field === 'y') void commitPlacement(placing.id, { y: value });
      else void commitPlacement(placing.id, { z: (floorOf(at.position[2]) ?? 0) + value });
    },
    [placing, commitPlacement, panoById, floorZ, floorOf],
  );

  const planAvoid = useMemo(
    () => (planDrawn && settings.panoramas ? markers.map((m) => m.placement.position) : []),
    [planDrawn, settings.panoramas, markers],
  );

  // Reproject the plan layer once there is something to project against.
  const planRevision = Number(hasContent) + 2 * Number(meshContent) + 4 * Number(splatLoaded) + 8 * Number(elementsShown);

  // ------------------------------------------------------------- legend --

  // What the colours mean, over the model (top left, beside the tools); off
  // where Vælg punkt is: inside a panorama, in Rapport, under a centre tile.
  const legendNow = settings.colour === 'rgb' ? null : legendKind(mode, sel.lens);
  // In Ressourcer a Materiale row outside the filtered set is faded, as its parts are ghosted.
  const legendSet = mode === 'ressourcer' ? (legendTypeIds ?? null) : null;
  // Etiket: the label cloud's classes in the palette the points are drawn with, in any mode.
  const labelLegendSource = colouring.colorMode === 'label' ? (labelSources.find((c) => c.name === labelSource) ?? null) : null;
  const legend = useMemo(() => {
    if (labelLegendSource?.labels) return labelLegendItems(labelLegendSource.labels, readLabelPalette());
    return legendNow ? legendItems(legendNow, data.types, legendSet) : [];
  }, [labelLegendSource, legendNow, data.types, legendSet]);
  const legendTitle = labelLegendSource ? `Etiket · ${labelLegendSource.name}` : legendNow ? LEGEND_TITLE[legendNow] : null;
  const showLegend = legendTitle !== null && !immersive && !quiet && !centreCovers && legend.length > 0;

  // --------------------------------------------------------- captureFor --

  // `?captureFor=<guid>` (from a material passport's thumbnail cell): grab
  // the view and PUT it as that passport's thumbnail, then go back.
  const captureFor = params.get('captureFor');
  const [capturing, setCapturing] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const captureThumbnail = useCallback(async () => {
    if (!captureFor || capturing) return;
    setCapturing(true);
    setCaptureError(null);
    try {
      const blob = (await viewportRef.current?.capture()) ?? null;
      if (!blob) throw new Error('Billedet af modellen kunne ikke læses');
      await api.uploadThumbnail(captureFor, new File([blob], 'thumbnail.png', { type: 'image/png' }));
      navigate('/data?tab=materials');
    } catch (error) {
      setCaptureError(error instanceof Error ? error.message : String(error));
      setCapturing(false);
    }
  }, [captureFor, capturing, navigate]);

  // ---------------------------------------------------------------- view --

  // Nothing at all to draw: no cloud, splat, mesh (the scan's or an
  // imported model's) or placeable panorama. A model-only case (a pure
  // Revit / IFC import, frontend-ayw.6) is not empty.
  // A failed mesh list counts as no meshes (the other layers decide).
  const nothingToDraw =
    cloudsRead.data !== undefined &&
    (meshesRead.data !== undefined || meshesRead.error !== undefined) &&
    !primary &&
    (gsplatsRead.data?.length ?? 0) === 0 &&
    (meshesRead.data?.length ?? 0) === 0 &&
    !hasElementRows &&
    allMarkers.length === 0;
  // Without a cloud the imported model is the building: its downloads drive
  // the status — loading only while an enabled category is in flight.
  const modelOnly = primary === null && !splatLoaded && !immersive;
  const modelState = modelOnly ? modelStatus(model, settings.modelLayers, planLock, modelLoads) : 'none';
  const scanMeshShown = settings.mesh && mesh !== null;
  const status = cloudsRead.error
    ? 'error'
    : nothingToDraw
      ? 'empty'
      : progress?.error || modelState === 'partial'
        ? 'streamError'
        : modelState === 'failed'
          ? 'modelError'
          : modelState === 'off' && !scanMeshShown
            ? 'modelOff'
            : (cloudsRead.data === undefined || (primary !== null && !hasContent) || modelState === 'loading') && !splatLoaded && !immersive
              ? 'loading'
              : null;

  return (
    <>
      <div ref={groundRef} className={styles.ground} role="region" aria-label="Bygningen som model">
        <Viewport
          ref={viewportRef}
          cameraKey="ground"
          background="surface"
          themeKey={themeTick}
          layers={layers}
          colorMode={colouring.colorMode === 'lut' && !colorLut ? 'rgb' : colouring.colorMode}
          colorLut={colorLut}
          pointSize={pointSize.size}
          pointSizeUnit={pointSize.unit}
          ghostStrength={GHOST_STRENGTH[settings.ghost]}
          ghostOpacity={ghostOpacityOf(settings.ghostTransparency)}
          frameToken={settings.frameNonce}
          projection={settings.projection}
          lighting={settings.lighting}
          view={settings.view}
          planLock={planLock}
          sectionHeight={sectionHeight}
          pickMode={picking}
          onPickPoint={handlePickPoint}
          onLayerProgress={handleProgress}
          onPageAdded={onPageAdded}
          onLayerRemoved={onLayerRemoved}
          pointerHits={measuring ? undefined : pointerHits}
          roomLabels={roomLabels}
          meshes={meshes}
          onMeshProgress={handleMeshProgress}
          elements={viewportElements}
          onElementsShown={setElementsShown}
          onPickElement={measuring ? undefined : handlePickElement}
          splats={splats}
          onSplatProgress={handleSplatProgress}
          panoramas={markers}
          showPanoramaMarkers={settings.panoramas && !immersive}
          activePanorama={
            activeMarker
              ? {
                  marker: activeMarker,
                  // Capped: a 12000 px equirect as a mipmapped texture is
                  // hundreds of MB of GPU memory no viewport can show.
                  imageUrl: api.panoramaImageUrl(activeMarker.id, { maxSize: 4096 }),
                }
              : null
          }
          onPanoramaState={setPanoramaState}
          onPickPanorama={handlePickPanorama}
          onCanvasClick={measuring ? measure.onCanvasClick : planDrawn && !cad ? handleCanvasClick : undefined}
          poseGraph={poseRead.data ?? null}
          poseGraphVisible={settings.posegraph && !immersive}
          clipping={clipping}
        />
        {wallSet && (
          <PlanWalls
            viewport={viewportRef}
            set={wallSet}
            revision={planRevision}
            selected={cad ? editor.selection : rediger && sel.element ? [sel.element] : undefined}
            cad={cad}
          />
        )}
        {planDrawn && (
          <PlanLayer
            viewport={viewportRef}
            slabs={planSlabSet}
            cutZ={plan.cutZ}
            spaces={planSpaceSet.shown}
            ghostSpaces={planSpaceSet.ghost}
            selectedSpace={rediger ? sel.element : selectedSpace}
            selectedSpaces={cad ? editor.selection : undefined}
            cad={cad}
            markers={cad ? NO_MARKERS : planMarkers}
            avoid={planAvoid}
            editId={placing?.kind === 'select' && placing.moving ? placing.id : null}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            onDragCancel={handleDragCancel}
            cancelToken={dragCancel}
            revision={planRevision}
          />
        )}
        {cad && planDrawn && (
          <>
            <GeometriLayer viewport={viewportRef} host={groundRef} editor={editor} storeyId={planStoreyId} z={floorZ ?? plan.cutZ ?? 0} />
            <CadToolLayer viewport={viewportRef} host={groundRef} editor={editor} storeyId={planStoreyId} z={floorZ ?? plan.cutZ ?? 0} />
          </>
        )}
        {(clipAvailable || measure.hover) && (
          <MeasureLayer
            viewport={viewportRef}
            state={measure.state}
            hover={measure.hover}
            editing={measuring}
            snapAt={measure.snapAt}
            plan={planLock}
            onMoveVertex={measure.moveVertex}
            onVertexClick={measure.vertexClick}
            setHover={measure.setHover}
            revision={planRevision}
          />
        )}
        {status && (
          <div className={styles.status} role="status">
            {status === 'loading' && <span>Model indlæses…</span>}
            {status === 'empty' && <span>Ingen punktsky i sagen endnu — kør pipelinen for at bygge modellen.</span>}
            {status === 'streamError' && <span>Modellen blev kun delvist indlæst.</span>}
            {status === 'modelError' && <span>Modellagene kunne ikke hentes fra serveren.</span>}
            {status === 'modelOff' && <span>Alle modellag er slået fra — slå dem til under Modelindstillinger › Lag.</span>}
            {status === 'error' && (
              <>
                <span>Modellen kunne ikke indlæses.</span>
                <Button variant="outline" size="sm" className={styles.retry} onClick={reloadClouds}>
                  Prøv igen
                </Button>
              </>
            )}
          </div>
        )}
        {!immersive && !quiet && !centreCovers && !cad && (
          <div className={styles.storeyPickerSlot}>
            <StoreyPicker storeys={storeys} value={elementFilter.storey} onChange={(storey) => dispatch({ type: 'buildingElementStorey', storey })} />
          </div>
        )}
        {(showLegend || pickAvailable || clipAvailable || planAvailable) && !cad && (
          <div className={styles.overlay} data-left={leftInset} data-phonebar={leftPhoneBar ? 'true' : undefined}>
            {showLegend && legendTitle && <CanvasLegend title={legendTitle} items={legend} />}
            {(pickAvailable || clipAvailable || planAvailable) && (
              <div className={styles.tools} role="toolbar" aria-label="Værktøjer i modellen">
                <div className={styles.toolRow}>
                  {planAvailable && (
                    <button
                      type="button"
                      className={`${styles.tool}${planLock ? ` ${styles.toolOn}` : ''}`}
                      aria-pressed={planLock}
                      onClick={() => setPlanLock(!planLock)}
                      title={
                        planLock
                          ? 'Lås kameraet op — tilbage til 3D-visningen'
                          : 'Lås kameraet til en plan set lige ovenfra — træk for at panorere, rul for at zoome'
                      }
                    >
                      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className={styles.toolIcon}>
                        <rect x="2" y="2" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M8 2v5h6M2 9h4v5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                      </svg>
                      {planLock ? 'Låst plan' : 'Plan'}
                    </button>
                  )}
                  {placeAvailable && (
                    <button
                      type="button"
                      className={`${styles.tool}${placingOn ? ` ${styles.toolOn}` : ''}`}
                      aria-pressed={placingOn}
                      onClick={() => {
                        if (placingOn) closePlacing();
                        else {
                          setMeasureTool(null);
                          setPlaceError(null);
                          setPlacing({ kind: 'select', id: null, moving: false });
                        }
                      }}
                      title={
                        placingOn
                          ? 'Afslut placering af 360°-billeder (Esc)'
                          : 'Flyt, drej eller tilføj 360°-billeder i planen'
                      }
                    >
                      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className={styles.toolIcon}>
                        <circle cx="6" cy="10" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M8.5 7.5 13 3M13 3H9.5M13 3v3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Placér 360°
                    </button>
                  )}
                  {pickAvailable && (
                    <button
                      type="button"
                      className={`${styles.tool}${picking ? ` ${styles.toolOn}` : ''}`}
                      aria-pressed={picking}
                      onClick={() => {
                        if (!picking) setMeasureTool(null);
                        setPicking(!picking);
                      }}
                      title={picking ? 'Annullér (Esc)' : 'Klik et punkt i modellen og se de billeder, der viser det'}
                    >
                      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className={styles.toolIcon}>
                        <circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M8 1v4M8 11v4M1 8h4M11 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      {picking ? 'Klik et punkt…' : 'Vælg punkt'}
                    </button>
                  )}
                  {clipAvailable && (
                    <MeasureButtons
                      tool={measuring ? measure.state.tool : null}
                      onTool={(tool) => {
                        if (tool !== null) {
                          setPicking(false);
                          if (placing) closePlacing();
                        }
                        setMeasureTool(tool);
                      }}
                    />
                  )}
                  {clipAvailable && (
                    <>
                      <button
                        type="button"
                        className={`${styles.tool}${clipToolShown ? ` ${styles.toolOn}` : ''}`}
                        aria-pressed={clipToolShown}
                        onClick={() => dispatch({ type: 'set', patch: { clipTool: !settings.clipTool } })}
                        title={
                          clipToolShown
                            ? 'Skjul klippeboksen (klipningen bliver)'
                            : 'Vis klippeboksen i modellen — træk i et håndtag for at skære modellen til'
                        }
                      >
                        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className={styles.toolIcon}>
                          <rect x="3" y="3" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="8" cy="3" r="1.5" fill="currentColor" />
                          <circle cx="13" cy="8" r="1.5" fill="currentColor" />
                          <circle cx="8" cy="13" r="1.5" fill="currentColor" />
                          <circle cx="3" cy="8" r="1.5" fill="currentColor" />
                        </svg>
                        Klippeboks
                      </button>
                      <button
                        type="button"
                        className={`${styles.tool}${clipUsable && settings.clipOn ? ` ${styles.toolOn}` : ''}`}
                        aria-pressed={settings.clipOn}
                        disabled={!clipUsable}
                        onClick={() => dispatch({ type: 'set', patch: { clipOn: !settings.clipOn } })}
                        title={
                          !clipUsable
                            ? 'Vis klippeboksen for at skære modellen til'
                            : settings.clipOn
                              ? 'Slå klipningen fra — vis hele modellen'
                              : 'Slå klipningen til — skær modellen til klippeboksen'
                        }
                      >
                        <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" className={styles.toolIcon}>
                          <circle cx="4.5" cy="12" r="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <circle cx="11.5" cy="12" r="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M6 10.5 12 2M10 10.5 4 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                        </svg>
                        Klip
                      </button>
                      {settings.clip !== null && (
                        <button
                          type="button"
                          className={styles.tool}
                          aria-label="Nulstil klippeboksen"
                          onClick={() => dispatch({ type: 'clip', box: null })}
                          title="Fjern klippeboksen — vis hele bygningen"
                        >
                          Nulstil
                        </button>
                      )}
                    </>
                  )}
                </div>
                {pickedElement && viewportElements?.visible && !rediger && (
                  <ElementChip
                    summary={elementsSummary(pickedElement, (id) => storeyById(storeys, id)?.name ?? null)}
                    details={elementDetails(pickedElement, { walls: building.walls, openings: building.openings, spaces: spatial.spaces, slabs: spatial.slabs })}
                    guid={pickedElement.guid}
                    onClear={() => setSelectedElement(null)}
                  />
                )}
                {pickAvailable && picked && (
                  <SourceImages visibility={visibility} error={visibilityError} onClose={closeSource} onOpenFrame={openFrame} />
                )}
                {clipAvailable && (measuring || measure.state.items.length > 0) && (
                  <MeasurePanel
                    state={measure.state}
                    tool={measuring ? measure.state.tool : null}
                    plan={planLock}
                    onRemove={(id) => measure.dispatch({ type: 'remove', id })}
                    onClear={() => measure.dispatch({ type: 'clear' })}
                  />
                )}
                {placingOn && (
                  <PlacementPanel
                    view={placementView}
                    error={placeError}
                    onPickFile={(file) => void handlePickFile(file)}
                    onToggleMove={() =>
                      setPlacing((p) => (p?.kind === 'select' && p.id !== null ? { ...p, moving: !p.moving } : p))
                    }
                    onOpen={(id) => enterPanorama(id)}
                    onCommit={handlePanelCommit}
                    onSubmitAdd={() => void submitAdd()}
                    onCancelUpload={cancelUpload}
                    onCancelAdd={() => {
                      setPlacing({ kind: 'select', id: null, moving: false });
                      setPlaceError(null);
                    }}
                    onClose={closePlacing}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {immersive && activePano && (
        <PanoramaOverlay
          panorama={activePano}
          placement={activeMarker?.placement ?? null}
          index={stepList.findIndex((m) => m.id === activePano.id) + 1}
          total={stepList.length}
          loading={panoramaState.loading}
          error={panoramaState.error}
          showGeometry={overlayGeometry}
          onShowGeometryChange={setOverlayGeometry}
          onStep={stepBy}
          onExit={() => enterPanorama(null)}
        />
      )}

      {canEdit && captureFor && (
        <div className={styles.captureBar} role="group" aria-label="Miniature">
          {captureError && (
            <span className={styles.captureError} role="alert">
              {captureError}
            </span>
          )}
          <Button
            variant="primary"
            size="sm"
            className={styles.capture}
            onClick={() => void captureThumbnail()}
            disabled={capturing}
            aria-busy={capturing || undefined}
          >
            {capturing ? 'Gemmer…' : 'Brug visningen som miniature'}
          </Button>
        </div>
      )}
    </>
  );
}
